import { useCallback, useMemo, useState } from "react"
import {
	IconCircleDashedFill18 as CircleDot,
	IconLayers3Fill18 as Layers,
} from "nucleo-ui-fill-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import { getCurrencySymbol } from "@productlane/lib/src/currency"
import { CustomerSizeIcon, CustomerTierIcon } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { DollarBillIcon } from "@productlane/ui/src/icons/project/dollarbill-icon"

import type { FilterType } from "./filter-popover-items/types"
import {
	FilterPopover,
	NumericComparisonFilterItems,
} from "@/components/filters"
import { useZeroWorkspace } from "@/lib/zero"
import { Owner } from "@/screens/app/threads/components/right-bar/customer-properties/icons/owner"
import { useCompaniesFilterState } from "./companies-filter-state"
import { OwnerFilterItems } from "./filter-popover-items/owner-filter-items"
import { SourceFilterItems } from "./filter-popover-items/source-filter-items"
import { StatusFilterItems } from "./filter-popover-items/status-filter-items"
import { TierFilterItems } from "./filter-popover-items/tier-filter-items"
import {
	useCompaniesSearchableItems,
	useFilteredCompaniesSearchItems,
} from "./use-companies-searchable-items"

export const FILTER_CATEGORIES: NestedCommandGroup<FilterType>[] = [
	{ type: "Statuses", label: "Status", Icon: CircleDot },
	{ type: "Tiers", label: "Tier", Icon: CustomerTierIcon },
	{ type: "Owners", label: "Owner", Icon: Owner },
	{ type: "Revenue", label: "Revenue", Icon: DollarBillIcon },
	{ type: "Size", label: "Size", Icon: CustomerSizeIcon },
	{ type: "CreatedBy", label: "Synced with", Icon: Layers },
]

const DOT_BADGE_CATEGORIES: ReadonlySet<FilterType> = new Set([
	"Revenue",
	"Size",
])

function NumericFilterSubPanel({
	activeFilter,
	onFilterItemSelect,
}: {
	activeFilter: "Revenue" | "Size"
	onFilterItemSelect: () => void
}) {
	const { revenue, setRevenue, size, setSize } = useCompaniesFilterState()
	const workspace = useZeroWorkspace()
	return (
		<Command shouldFilter={false}>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandGroup>
					{activeFilter === "Revenue" && (
						<NumericComparisonFilterItems
							value={revenue}
							onChange={setRevenue}
							onSelect={onFilterItemSelect}
							placeholder="Enter revenue..."
							prefix={getCurrencySymbol(workspace?.revenueCurrency)}
						/>
					)}
					{activeFilter === "Size" && (
						<NumericComparisonFilterItems
							value={size}
							onChange={setSize}
							onSelect={onFilterItemSelect}
							placeholder="Enter company size..."
						/>
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

function FilterSubPanel({
	activeFilter,
	query,
	setQuery,
	onFilterItemSelect,
}: {
	activeFilter: FilterType
	query: string
	setQuery: (q: string) => void
	onFilterItemSelect: () => void
}) {
	if (activeFilter === "Revenue" || activeFilter === "Size") {
		return (
			<NumericFilterSubPanel
				activeFilter={activeFilter}
				onFilterItemSelect={onFilterItemSelect}
			/>
		)
	}

	return (
		<Command>
			<CommandInput
				key={activeFilter}
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{activeFilter === "Tiers" && (
						<TierFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Statuses" && (
						<StatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Owners" && (
						<OwnerFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "CreatedBy" && (
						<SourceFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function CompaniesFilter() {
	const [globalQuery, setGlobalQuery] = useState("")
	const filterState = useCompaniesFilterState()

	const counts = useMemo(
		() =>
			({
				Statuses: filterState.statuses.length,
				Tiers: filterState.tiers.length,
				Owners: filterState.ownerIds.length,
				Revenue: filterState.revenue ? 1 : 0,
				Size: filterState.size ? 1 : 0,
				CreatedBy: filterState.sources.length,
			}) as Record<FilterType, number>,
		[filterState],
	)

	const onCategorySelect = useCallback(() => {
		setGlobalQuery("")
	}, [])

	const searchableItems = useCompaniesSearchableItems({
		onCategorySelect: onCategorySelect as () => void,
	})
	const filteredSearchItems = useFilteredCompaniesSearchItems(
		globalQuery,
		searchableItems,
		filterState,
	)

	const renderSubPanel = useCallback(
		(
			group: FilterType,
			query: string,
			setQuery: (q: string) => void,
			onFilterItemSelect: () => void,
		) => (
			<FilterSubPanel
				activeFilter={group}
				query={query}
				setQuery={setQuery}
				onFilterItemSelect={onFilterItemSelect}
			/>
		),
		[],
	)

	const subPanelWidth = useCallback(
		(group: FilterType) =>
			group === "Revenue" || group === "Size" ? "w-80" : "w-64",
		[],
	)

	return (
		<FilterPopover<FilterType>
			categories={FILTER_CATEGORIES}
			counts={counts}
			renderSubPanel={renderSubPanel}
			searchableItems={searchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			dotBadgeCategories={DOT_BADGE_CATEGORIES}
			subPanelWidthClassName={subPanelWidth}
			tooltipLabel="Filter customers"
		/>
	)
}
